/**
 * Quem é esta sessão de teste, e se ela deve gravar alguma coisa.
 *
 * A gravação NEVER começa sozinha: exige `?t=T-4F2` na URL, o código que o
 * `pnpm probe session` imprime. É a decisão de escopo — só teste MARCADO — e ela
 * aparece aqui como a primeira porta, mesmo o servidor já recusando lote sem
 * código: recusar no cliente economiza a banda do jogador, e recusar no servidor
 * é o que torna a regra verdadeira (invariante 2).
 */
import { isTestCode, type SessionResume } from '@game/telemetry';

export interface TelemetrySession {
  /** Código da sessão de teste. `undefined` quando NEVER se deve gravar nada. */
  readonly test: string | undefined;
  /**
   * Id opaco desta ABERTURA de página. Sorteado a cada carregamento e NEVER
   * guardado: ele separa quatro aparelhos numa mesma sessão de teste.
   */
  readonly clientId: string;
  /**
   * Id opaco da ABA, e ele SOBREVIVE ao recarregar (`ADR-0023`). É o que permite
   * contar segunda partida VOLUNTÁRIA, que é o Portão 02: `jogar de novo` chama
   * `location.reload()`, e sem isto quem toca o botão volta como pessoa nova.
   *
   * Mora em `sessionStorage`, e a escolha é a linha entre MEDIR e RASTREAR:
   * `sessionStorage` morre com a aba, então ele junta as rodadas de UMA sessão de
   * teste e NEVER reconhece a mesma pessoa amanhã. `localStorage` reconheceria, e
   * é por isso que NEVER é usado aqui.
   */
  readonly tabId: string;
  /** Qual rodada desta aba, a partir de 1. */
  readonly run: number;
  /** De onde veio esta abertura. `retry` é o botão, e é o número do portão. */
  readonly resumedBy: SessionResume;
}

/** Base32 legível, sem `I` nem `O`: o código é lido em voz alta e digitado à mão. */
function randomId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const TAB_KEY = 'pulso.probe.tab';
const RUN_KEY = 'pulso.probe.run';
const RETRY_KEY = 'pulso.probe.retry';

/**
 * `sessionStorage` pode LANÇAR, e NEVER só devolver vazio: Safari em navegação
 * privada e aba com armazenamento bloqueado jogam ao simples acesso. Cair aqui
 * NEVER pode derrubar o jogo — a telemetria é observação, e observação que
 * quebra o observado é pior que observação nenhuma. Sem armazenamento, a aba
 * vira uma rodada 1 solitária, que é o comportamento anterior a `ADR-0023`.
 */
function store(): Storage | undefined {
  try {
    // `globalThis`, e NEVER `window`: este arquivo é testado sem browser, e ler
    // `window` num ambiente que NEVER o tem lança ANTES do `try` poder ajudar em
    // qualquer coisa útil.
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * Marca que a PRÓXIMA abertura desta aba veio do botão `jogar de novo`.
 * Chamado ANTES do `location.reload()`, e é a única coisa que separa intenção de
 * recarga — sem ela, F5 e "quero jogar de novo" são o mesmo evento.
 */
export function markRetry(): void {
  try {
    store()?.setItem(RETRY_KEY, '1');
  } catch {
    // Aba sem armazenamento: a rodada seguinte conta como `reload`, e NEVER
    // como portão. Perder para baixo é a direção certa de errar num portão.
  }
}

export function readSession(search: string = location.search): TelemetrySession {
  const raw = new URLSearchParams(search).get('t');
  const memory = store();

  let tabId = memory?.getItem(TAB_KEY) ?? undefined;
  const previous = Number.parseInt(memory?.getItem(RUN_KEY) ?? '', 10);
  const retried = memory?.getItem(RETRY_KEY) === '1';

  // Aba sem `tabId` é aba nova, e rodada 1 — mesmo que o contador tenha
  // sobrevivido sozinho por algum motivo. Os dois andam juntos ou NEVER andam.
  const fresh = tabId === undefined || !Number.isInteger(previous) || previous < 1;
  if (fresh) tabId = `w-${randomId()}`;
  const run = fresh ? 1 : previous + 1;

  try {
    memory?.setItem(TAB_KEY, tabId ?? '');
    memory?.setItem(RUN_KEY, String(run));
    memory?.removeItem(RETRY_KEY);
  } catch {
    // Ver `store`: sem armazenamento a aba NEVER acumula rodada, e o laudo mostra
    // rodada 1. É o dado FALTANDO, e NEVER um dado errado.
  }

  return {
    test: isTestCode(raw) ? raw : undefined,
    clientId: `c-${randomId()}`,
    tabId: tabId ?? `w-${randomId()}`,
    run,
    resumedBy: fresh ? 'fresh' : retried ? 'retry' : 'reload',
  };
}
